import { useEffect, useState } from 'react';
import { ImageUp, Loader2, ScanText } from 'lucide-react';
import OcrReviewTable from './OcrReviewTable';
import { imageFileToBase64 } from '../../utils/imageToBase64';

const API = process.env.REACT_APP_BACKEND_URL;
export const OCR_COMPANIES = [
  { key: 'musicmagpie', label: 'MusicMagpie' },
  { key: 'webuybooks', label: 'WeBuyBooks' },
  { key: 'worldofbooks', label: 'World of Books' },
];

/** Upload basket screenshots -> AI reads them -> user confirms rows -> onConfirm(company, rows). */
export default function ScreenshotReader({ fixedCompany, batchId, onConfirm, confirmLabel, busy }) {
  const [company, setCompany] = useState(fixedCompany || 'musicmagpie');
  const [files, setFiles] = useState([]);
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState(null);
  const [reading, setReading] = useState(false);
  const [err, setErr] = useState('');
  const [left, setLeft] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/assistant/usage`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setLeft(d.unlimited ? null : d.ocr_left));
  }, []);

  const read = async () => {
    setErr('');
    setReading(true);
    try {
      const images = await Promise.all(files.map(imageFileToBase64));
      const res = await fetch(`${API}/api/ocr/extract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ company, images, batch_id: batchId || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not read screenshots.');
      setRows(data.rows.map((r) => ({ ...r, include: r.status === 'matched' && r.price != null })));
      setMeta({ summary: data.summary, totals: data.basket_totals });
      setLeft(data.screenshots_left_today ?? null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setReading(false);
    }
  };

  const setRow = (i, patch) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const chosen = (rows || []).filter((r) => r.include && r.match_barcode && r.price != null);
  const reset = () => { setRows(null); setMeta(null); setFiles([]); };

  return (
    <div data-testid="screenshot-reader">
      {!rows ? (
        <UploadStep {...{ fixedCompany, company, setCompany, files, setFiles, read, reading, left }} />
      ) : (
        <>
          <ReviewSummary meta={meta} />
          <OcrReviewTable rows={rows} setRow={setRow} allowManualBarcode={!batchId} />
          <div className="flex gap-2 justify-end mt-4">
            <button onClick={reset} className="px-4 py-2 rounded-full text-sm font-bold border border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="ocr-start-over-btn">
              Start over
            </button>
            <button
              onClick={() => onConfirm(company, chosen)}
              disabled={chosen.length === 0 || busy}
              className="px-5 py-2 rounded-full text-sm font-bold disabled:opacity-50"
              style={{ background: 'linear-gradient(90deg,#00D26A,#FFD700)', color: '#050505' }}
              data-testid="ocr-confirm-btn"
            >
              {busy ? 'Saving…' : `${confirmLabel} (${chosen.length})`}
            </button>
          </div>
        </>
      )}
      {err && <p className="text-sm mt-3" style={{ color: '#E04A3F' }} data-testid="ocr-error">{err}</p>}
    </div>
  );
}

function UploadStep({ fixedCompany, company, setCompany, files, setFiles, read, reading, left }) {
  return (
    <div className="space-y-4">
      {!fixedCompany && (
        <div className="flex gap-2 flex-wrap" data-testid="ocr-company-picker">
          {OCR_COMPANIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setCompany(c.key)}
              className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${company === c.key ? 'border-[#00D26A] text-[#00D26A] bg-[#00D26A]/10' : 'border-zinc-700 text-zinc-300 hover:bg-zinc-800'}`}
              data-testid={`ocr-company-${c.key}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}
      <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-zinc-700 rounded-xl p-8 cursor-pointer hover:border-[#00D26A] transition-colors" data-testid="ocr-dropzone">
        <ImageUp className="w-8 h-8 text-zinc-500" />
        <span className="text-sm text-zinc-300">{files.length ? `${files.length} screenshot(s) selected` : 'Tap to choose basket screenshots (up to 8)'}</span>
        <span className="text-xs text-zinc-500">Screenshots are read then discarded — never stored.</span>
        {left !== null && <span className="text-xs text-[#FFD700]" data-testid="ocr-allowance">{left} screenshot read{left === 1 ? '' : 's'} left today</span>}
        <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 8))} data-testid="ocr-file-input" />
      </label>
      <button
        onClick={read}
        disabled={!files.length || reading || left === 0}
        className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-bold disabled:opacity-50"
        style={{ background: 'linear-gradient(90deg,#00D26A,#FFD700)', color: '#050505' }}
        data-testid="ocr-read-btn"
      >
        {reading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanText className="w-4 h-4" />}
        {reading ? 'Reading screenshots…' : 'Read screenshots'}
      </button>
    </div>
  );
}

function ReviewSummary({ meta }) {
  if (!meta) return null;
  const { matched, unsure, unmatched } = meta.summary;
  return (
    <div className="flex flex-wrap gap-3 text-xs mb-3" data-testid="ocr-summary">
      <span className="text-[#00D26A]">{matched} matched</span>
      <span className="text-[#FFD700]">{unsure} to check</span>
      <span className="text-[#E04A3F]">{unmatched} not found</span>
      {meta.totals?.length > 0 && <span className="text-zinc-400">Basket total seen: £{meta.totals.map((t) => Number(t).toFixed(2)).join(', £')}</span>}
    </div>
  );
}
